import { useEffect, useMemo, useState } from 'react';
import { createSearchService } from '../lib/searchService';
import { SearchResult } from '../types/content';
import { useExamples, useNews } from './useContent';

/**
 * Unified search hook for header search functionality
 */
export function useUnifiedSearch(query: string) {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  
  const { posts } = useNews();
  const { examples } = useExamples();
  
  const searchService = useMemo(() => {
    return createSearchService(posts, examples);
  }, [posts, examples]);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }

    const performSearch = () => {
      setLoading(true);
      try {
        const searchResults = searchService.searchAll(query, 8);
        setResults(searchResults);
      } catch (error) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(performSearch, 300);
    return () => clearTimeout(debounce);
  }, [query, searchService]);

  return { results, loading };
}

/**
 * Advanced news filtering hook
 */
export function useNewsFilter() {
  const { posts, loading, error } = useNews();
  const { examples } = useExamples();
  
  const searchService = useMemo(() => {
    return createSearchService(posts, examples);
  }, [posts, examples]);

  const filterOptions = useMemo(() => {
    return searchService.getFilterOptions();
  }, [searchService]);

  const filterNews = (options: {
    query?: string;
    topic?: string;
    source?: string;
  }) => {
    return searchService.filterNews({
      query: options.query,
      topics: options.topic && options.topic !== 'all' ? [options.topic] : undefined,
      sources: options.source && options.source !== 'all' ? [options.source] : undefined,
      sortBy: 'date'
    });
  };

  return {
    posts,
    loading,
    error,
    filterNews,
    sources: filterOptions.sources,
    topics: filterOptions.topics
  };
}

/**
 * Advanced examples filtering hook
 */
export function useExamplesFilter() {
  const { posts } = useNews();
  const { examples, loading, error } = useExamples();
  
  const searchService = useMemo(() => {
    return createSearchService(posts, examples);
  }, [posts, examples]);

  const filterOptions = useMemo(() => {
    return searchService.getFilterOptions();
  }, [searchService]);

  const filterExamples = (options: {
    query?: string;
    goal?: string;
    audience?: string;
    complexity?: string;
  }) => {
    return searchService.filterExamples({
      query: options.query,
      goals: options.goal && options.goal !== 'all' ? [options.goal] : undefined,
      audiences: options.audience && options.audience !== 'all' ? [options.audience] : undefined,
      complexities: options.complexity && options.complexity !== 'all' ? [options.complexity] : undefined
    });
  };

  return {
    examples,
    loading,
    error,
    filterExamples,
    goals: filterOptions.goals,
    audiences: filterOptions.audiences,
    complexities: filterOptions.complexities
  };
}